import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { jobSite } from '../../lib/job-site.js';
import { ConfirmDialog } from '../ui/confirm-dialog.js';
import {
  AlertCircle,
  ArrowLeft,
  MapPin,
  MapPinned,
  Navigation,
  MessageCircle,
  Phone,
} from 'lucide-react';
import { api } from '../../lib/api.js';
import { readAssignment, windowLabel } from '../../lib/dispatch-board.js';
import { useFieldJob } from '../../lib/field-job.js';
import { ACTIVE_JOB_QUERY_KEY } from '../../lib/field-time.js';

/**
 * On-the-way card — deep-blue full-bleed treatment matching the mockup's
 * `.big-card` pattern. Shows the destination, customer, ETA, and quick
 * actions (navigate / SMS the customer / arrive).
 *
 * The "Arrive" button just flips state — real implementations would also
 * push a GPS ping. We don't yet have a server-side endpoint for tech
 * presence; that's a Phase 5 follow-up.
 */
export function OnwayScreen() {
  const orderId = useFieldJob((s) => s.currentOrderId);
  const arrive = useFieldJob((s) => s.arrive);
  const cancel = useFieldJob((s) => s.cancelJob);
  const queryClient = useQueryClient();
  // The back arrow stops the travel timer and un-starts the job, so it
  // asks first — one stray thumb used to cancel the trip outright.
  const [confirmCancel, setConfirmCancel] = useState(false);

  const arriveMutation = useMutation({
    mutationFn: () => api.field.phaseChange({ phase: 'on_site' }),
    onSuccess: () => {
      arrive();
      void queryClient.invalidateQueries({ queryKey: ACTIVE_JOB_QUERY_KEY });
    },
  });

  // Back-to-day from Onway requires closing the server-side TimeEntry
  // and clearing AppUser.activeJobSpireId — without this, the open
  // `traveling` entry stays around and EVERY subsequent stop the tech
  // tries returns 409 JOB_ALREADY_ACTIVE. finalPhase='scheduled' so the
  // order ends up back where the dispatcher placed it.
  const cancelMutation = useMutation({
    mutationFn: () => api.field.stop({ finalPhase: 'scheduled' }),
    onSuccess: () => {
      cancel();
      void queryClient.invalidateQueries({ queryKey: ACTIVE_JOB_QUERY_KEY });
    },
    onError: () => {
      // Server-side failure is rare but the user shouldn't be trapped —
      // fall through to the local reset. The reconciliation effect in
      // /jobs picks up any leftover server entry on next mount.
      cancel();
    },
  });

  const orderQuery = useQuery({
    queryKey: ['sales-orders', 'detail', orderId],
    queryFn: ({ signal }) => api.salesOrders.getById(orderId!, signal),
    enabled: orderId !== null,
    staleTime: 30_000,
  });

  if (orderId === null) {
    // Defensive — orchestrator should never render this without a current job.
    return <div className="text-sm text-ink-muted">No active job.</div>;
  }

  const order = orderQuery.data;
  const customerName = order?.customer.name ?? 'Loading…';
  const description =
    order?.items[0]?.description ?? order?.customer.userDef2 ?? 'Service call';
  // Ship-to = the service address (lib/job-site.ts). Navigate gets the FULL
  // address — street + city alone is ambiguous for rural routes.
  const site = order ? jobSite(order) : null;
  const address = order ? (site?.addressLine ?? 'No ship-to address on this order') : 'Loading address…';
  // Customer phone — the SO's `contactPhone` is the per-job contact;
  // SalesOrderAddress doesn't carry phone in our DTO, so this is the
  // canonical source. Normalised to digits-plus-leading-plus so `tel:`
  // / `sms:` hand off cleanly to the dialer / Messages.
  const phoneNumber = order?.contactPhone
    ? order.contactPhone.replace(/[^\d+]/g, '')
    : null;
  // Scheduled window (8a – 12p style) from the dispatcher's FOP_START /
  // FOP_DURATION. Falls back to the bare required date when the order
  // isn't on the board for today.
  const scheduledLabel = order ? buildScheduledLabel(order.udf ?? {}, order.requiredDate) : null;

  return (
    <div className="space-y-3">
      <header className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setConfirmCancel(true)}
          disabled={cancelMutation.isPending}
          aria-label="Back to day"
          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-ink-muted hover:bg-surface-muted disabled:opacity-50"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="text-sm font-medium text-ink">On the way</div>
        {phoneNumber ? (
          <a
            href={`tel:${phoneNumber}`}
            aria-label={`Call ${customerName}`}
            title={`Call ${phoneNumber}`}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-ink-muted hover:bg-surface-muted"
          >
            <Phone className="h-5 w-5" />
          </a>
        ) : (
          <div className="h-9 w-9" aria-hidden />
        )}
      </header>

      <div
        className="rounded-xl p-4 text-white space-y-3"
        style={{ backgroundColor: 'var(--color-live-card-bg)' }}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/20">
            En route
          </span>
          {scheduledLabel && (
            <span className="text-xs opacity-85">Scheduled {scheduledLabel}</span>
          )}
        </div>
        <div>
          <div className="text-lg font-semibold leading-tight">{customerName}</div>
          {site?.contactName && (
            <div className="text-sm font-medium opacity-95">Ship-to: {site.contactName}</div>
          )}
          <div className="text-sm opacity-90">{description}</div>
        </div>
        <div className="flex items-center gap-1.5 text-sm opacity-90">
          <MapPin className="h-4 w-4" aria-hidden />
          <span className="truncate">{address}</span>
        </div>
        <div className="grid grid-cols-2 gap-2 pt-1">
          <ActionGhost label="Navigate" Icon={Navigation} onClick={() => openMaps(site?.mapsQuery ?? '')} />
          <ActionGhost
            label="Text customer"
            Icon={MessageCircle}
            onClick={() => openSms(phoneNumber, customerName)}
            disabled={!phoneNumber}
            title={
              phoneNumber
                ? `Text ${phoneNumber}: "On my way, see you shortly."`
                : 'No customer phone on this order'
            }
          />
        </div>
        <button
          type="button"
          onClick={() => arriveMutation.mutate()}
          disabled={arriveMutation.isPending}
          className="w-full inline-flex items-center justify-center gap-2 rounded-md px-3 py-2.5 text-sm font-semibold bg-white disabled:opacity-60"
          style={{ color: 'var(--color-live-card-bg)' }}
        >
          <MapPinned className="h-4 w-4" />
          {arriveMutation.isPending ? "Marking on site…" : "I've arrived"}
        </button>
        {/* Failed arrive must not look like a no-op — the button above
           re-enables; this strip says why. Matches the clockOutError
           treatment in onsite-screen (Feedback's light-surface tokens
           don't read on this deep-blue card). */}
        {arriveMutation.isError && (
          <div
            className="rounded-md px-3 py-2 text-sm flex items-start gap-2"
            style={{
              backgroundColor: 'rgba(239,68,68,0.18)',
              color: '#FCA5A5',
              border: '1px solid rgba(239,68,68,0.5)',
            }}
            role="alert"
          >
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden />
            <div className="flex-1 leading-tight">
              <div className="font-medium">Couldn&apos;t mark you on site</div>
              <div className="text-[12px] opacity-90">
                {arriveMutation.error?.message ?? 'Try again.'}
              </div>
            </div>
          </div>
        )}
      </div>

      <p className="text-center text-[11px] text-ink-muted">
        Tapping "I've arrived" updates the dispatch board with your on-site status.
      </p>

      {confirmCancel && (
        <ConfirmDialog
          title="Stop this trip?"
          message={`This stops your travel timer and puts ${order?.orderNo ? `job ${order.orderNo}` : 'the job'} back on your day as scheduled. You can start it again from My Day.`}
          confirmLabel="Stop trip"
          cancelLabel="Keep going"
          pending={cancelMutation.isPending}
          onConfirm={() => cancelMutation.mutate()}
          onCancel={() => setConfirmCancel(false)}
        />
      )}
    </div>
  );
}

function ActionGhost({
  label,
  Icon,
  onClick,
  disabled,
  title,
}: {
  label: string;
  Icon: typeof Navigation;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="inline-flex flex-col items-center justify-center gap-1 rounded-md py-2 text-xs font-medium border border-white/30 bg-white/10 hover:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed"
    >
      <Icon className="h-5 w-5" aria-hidden />
      <span>{label}</span>
    </button>
  );
}

function openMaps(address: string) {
  if (!address) return;
  const q = encodeURIComponent(address);
  window.open(`https://www.google.com/maps/search/?api=1&query=${q}`, '_blank', 'noopener');
}

/**
 * Build the scheduled-window label for the en-route hero. Returns the
 * dispatched window if FOP_START/FOP_DURATION are set (e.g. "8a – 12p"),
 * else the bare required date, else null. Matches the label format used
 * on the Day-screen stop cards.
 */
function buildScheduledLabel(udf: Record<string, string>, requiredDate: string | null): string | null {
  const a = readAssignment(udf);
  if (a) return windowLabel(a.startHour, a.duration);
  if (requiredDate) return requiredDate.slice(5);
  return null;
}


/**
 * Open the phone's SMS app prefilled with an "On my way" message. iOS uses
 * `sms:NUMBER&body=…`, Android uses `sms:NUMBER?body=…` — `?body=` works
 * across both Safari iOS 16+ and Chrome Android, so we use the standard
 * URI query form.
 */
function openSms(phone: string | null, customerName: string) {
  if (!phone) return;
  const greeting = customerName && customerName !== 'Loading…' ? `Hi ${customerName.split(' ')[0]}, ` : '';
  const body = `${greeting}on my way, see you shortly.`;
  window.location.href = `sms:${phone}?body=${encodeURIComponent(body)}`;
}

